import { Info } from "@phosphor-icons/react";
import clsx from "clsx";

import RenderIf from "common/components/RenderIf";
import Progress from "common/components/ui/Progress";
import Skeleton from "common/components/ui/Skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";

import { useCampaignContext } from "../../../context/CampaignContext";
import useCampaignCompletion from "../../../datahooks/useCampaignCompletion";
import useCampaignMetrics from "../../../datahooks/useCampaignMetrics";

interface StatsItemProps {
  label: string;
  value: string | number;
  tooltipText: string;
  progress?: number;
  isLoading?: boolean;
}

function StatItem({
  label,
  value,
  tooltipText,
  progress = null,
  isLoading = false,
}: StatsItemProps) {
  return (
    <div
      className={clsx(
        "flex-1 rounded-5xl bg-whiteGray px-5 pt-5",
        progress !== null ? "pb-2" : "pb-8",
      )}
    >
      <div className="mb-4 flex items-center gap-x-1">
        <h4 className="text-md-medium">{label}</h4>
        <Tooltip>
          <TooltipTrigger>
            <Info className="size-5 fill-black-500" weight="fill" />
          </TooltipTrigger>
          <TooltipContent>{tooltipText}</TooltipContent>
        </Tooltip>
      </div>

      {isLoading ? (
        <Skeleton className="h-12 w-24" />
      ) : (
        <span className="text-5xl-medium">{value}</span>
      )}

      <RenderIf condition={progress !== null}>
        <Progress value={progress} className="mt-2" />
      </RenderIf>
    </div>
  );
}

export default function CampaignStats() {
  const { id, outreach_type: outreachType } = useCampaignContext();

  const { completion, isLoadingCompletion } = useCampaignCompletion(id);
  const { campaignMetrics, isLoadingCampaignMetrics } = useCampaignMetrics(id);

  const {
    accepted_connections: acceptedConnections,
    sent_connections: sentConnections,
    sent_inmails: sentInmails,
    message_requests: messageRequests,
    inmail_replies: inmailReplies,
    sent_messages: sentMessages,
    replies,
  } = campaignMetrics ?? {};

  const isDrip = outreachType === "drip";

  let totalInteractions: string | number = "-";
  let leadsGained: string | number = "-";
  if (campaignMetrics) {
    totalInteractions = isDrip
      ? sentMessages
      : sentConnections + sentInmails + messageRequests;

    leadsGained =
      outreachType === "connect"
        ? acceptedConnections + inmailReplies
        : replies;
  }

  return (
    <section className="flex flex-col gap-3 md:flex-row">
      <StatItem
        label={`${isDrip ? "Replies" : "Leads"} gained`}
        value={leadsGained}
        tooltipText={
          isDrip
            ? "How many message replies you received from this campaign"
            : "How many leads you gained in this timeframe. A lead is anyone who has accepted a connection request, or who has replied to any of your inmails, messages, or message requests"
        }
        isLoading={isLoadingCampaignMetrics}
      />
      <StatItem
        label="Total interactions done"
        value={totalInteractions}
        tooltipText="The total sum of all interactions you performed through Aimfox campaigns. Includes profile views, connection requests, inmails, messages and message requests"
        isLoading={isLoadingCampaignMetrics}
      />
      <StatItem
        label="Campaign progress"
        value={
          typeof completion === "number"
            ? `${Math.floor(completion * 100)}%`
            : "-"
        }
        tooltipText="The current progress of your campaign. The campaign is considered completed once it has sent out all invites, inmails, and messages"
        progress={completion ? completion * 100 : 0}
        isLoading={isLoadingCompletion}
      />
    </section>
  );
}
